import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

/**
 * Inbox list controller — opens a clicked entry into the reading pane when one
 * is on screen, or via full navigation, and highlights the active row. Search is
 * server-side (see `inbox-search`); bulk actions live in the shared
 * `mass-action` controller driving the always-visible row checkboxes.
 */
export default class InboxController extends Controller {
  static targets = ["entry"];
  static values = { openEntry: String };

  declare entryTargets: HTMLElement[];
  declare openEntryValue: string;

  /**
   * Highlight a server pre-selected row (e.g. the walkthrough's sample
   * announcement rendered straight into the reading pane) without navigating or
   * marking it read, so the row matches the already-open pane.
   */
  connect(): void {
    if (!this.openEntryValue) return;

    const row = document.getElementById(this.openEntryValue);
    if (row) this.highlightActive(row);
  }

  /**
   * Navigate to the source of a clicked inbox entry.
   * Mark-as-read is handled server-side via the mark_as_read action.
   */
  visitEntry(event: Event): void {
    const target = event.currentTarget as HTMLElement;
    const entryId = target.dataset.entryId;
    const entryUrl = target.dataset.entryUrl;
    const entryFrame = target.dataset.entryFrame;

    if (!entryId || !entryUrl) return;

    // Loading the entry into the reading pane (or navigating to its source)
    // marks it read server-side, so no separate mark-as-read call is needed.
    this.highlightActive(target);
    this.markRead(target);

    // A rendered reading pane takes the entry; a sheet too narrow for two panes
    // hides it, and the row pushes a full-screen reader via a normal navigation.
    if (entryFrame && this.visitFrame(entryFrame, entryUrl)) return;

    Turbo.visit(entryUrl);
  }

  /**
   * Handle keyboard activation (Enter/Space) on inbox entries for accessibility.
   */
  visitEntryKeydown(event: KeyboardEvent): void {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.visitEntry(event);
    }
  }

  /**
   * Prevent event propagation for child interactive elements (buttons).
   */
  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  private highlightActive(target: HTMLElement): void {
    this.entryTargets.forEach((entry) => entry.removeAttribute("data-active"));
    target.setAttribute("data-active", "true");
  }

  /**
   * Flip a row to its read appearance as soon as it's opened. The server marks
   * the entry read and broadcasts the same change, but that only lands over the
   * cable — the reading-pane response itself carries no row update — so without
   * this the row keeps its unread dot and weight until the broadcast arrives.
   * Mirrors what inbox/_entry_title and inbox/_read_toggle render when read.
   */
  private markRead(row: HTMLElement): void {
    row.querySelector("[data-inbox-dot]")?.classList.add("hidden");
    row.querySelector("[data-inbox-title]")?.classList.replace("font-semibold", "font-normal");
    row.querySelector("[data-inbox-mark-read]")?.classList.add("hidden");
    row.querySelector("[data-inbox-mark-unread]")?.classList.remove("hidden");
  }

  /**
   * Point the reading pane at the entry. Returns false when the pane is absent
   * or not rendered — which is how a sheet below the two-pane step (@4xl) asks
   * for the full-page reader instead.
   */
  private visitFrame(frameId: string, url: string): boolean {
    const frame = document.getElementById(frameId);
    if (!frame || !(frame.checkVisibility?.() ?? frame.offsetParent !== null)) return false;

    frame.setAttribute("src", url);
    return true;
  }
}
